import { FC, MouseEvent, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { detectDirection } from '@libs/utils';
import { DotsThreeIcon, TextAlignLeftIcon, TrashIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

/** Shared by the parent's hover rule and the button it reveals. */
const ACTIONS_CLASS = 'chat-history-card-actions';

interface ChatHistoryCardProps {
  title: string;
  isActive?: boolean;
  isStreaming?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
}

const ChatHistoryButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.875, 1.25),
  borderRadius: theme.spacing(1),
  border: '1px solid',
  borderColor: isActive ? '#333333' : 'transparent',
  background: isActive
    ? [
        'linear-gradient(to bottom, white 100%, rgba(255,255,255,0.85) 78%, rgba(255,255,255,0) 90%)',
        theme.direction === 'rtl'
          ? 'linear-gradient(to right, white 0%, rgba(255,255,255,0) 35%)'
          : 'linear-gradient(to left, white 0%, rgba(255,255,255,0) 35%)',
        theme.direction === 'rtl'
          ? 'linear-gradient(to left, rgba(255,105,180,0.25) 40%, rgba(50, 213, 188, 0.25) 60%)'
          : 'linear-gradient(to right, rgba(255,105,180,0.25) 40%, rgba(50, 213, 188, 0.25) 60%)',
      ].join(', ')
    : 'transparent',
  backgroundSize: isActive ? '100% 100%, 55% 25%, 55% 25%' : 'auto',
  backgroundPosition: isActive
    ? theme.direction === 'rtl'
      ? 'top right, bottom right, bottom right'
      : 'top left, bottom left, bottom left'
    : 'center',
  backgroundRepeat: 'no-repeat',
  backgroundColor: isActive ? 'white' : 'transparent',
  justifyContent: 'flex-start',
  minHeight: 0,
  gap: theme.spacing(1),
  '&:hover': {
    borderColor: isActive ? '#333333' : '#E0E0E0',
    backgroundColor: isActive ? 'white' : '#F7F7F7',
  },
}));

export const ChatHistoryCard: FC<ChatHistoryCardProps> = observer(
  ({ title, isActive, isStreaming, onClick, onDelete }) => {
    const { isRtl } = useDirection();
    const { t } = useTrans();
    const dynamicDirection = detectDirection(title);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    // The kebab has to sit outside the card button — a <button> inside a
    // <button> is invalid markup and swallows the inner click. It stays
    // available while a stream is running: the panel tears the stream down
    // before deleting, and hiding the control here would leave a user with a
    // long generation no way to delete that conversation at all.
    const showActions = Boolean(onDelete);

    const openMenu = (event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget);
    const closeMenu = () => setMenuAnchor(null);

    return (
      <Box
        sx={{
          position: 'relative',
          // Keep the kebab visible while its menu is open, otherwise moving the
          // pointer onto the menu hides the control it is anchored to.
          [`&:hover .${ACTIONS_CLASS}, &:focus-within .${ACTIONS_CLASS}`]: {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }}
      >
        <ChatHistoryButton
          variant="outlined"
          fullWidth
          onClick={onClick}
          isActive={isActive}
          sx={showActions ? { paddingInlineEnd: 4 } : undefined}
        >
          <TextAlignLeftIcon
            style={{ transform: isRtl ? 'scaleX(-1)' : 'none', flexShrink: 0 }}
            color="#ADADAD"
            size={15}
          />
          <NoFlipBox mode={dynamicDirection} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              style={{
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
              }}
              variant="body2"
              sx={{
                textAlign: 'start',
                overflow: 'hidden',
                display: '-webkit-box',
                fontWeight: 500,
                fontSize: '0.8125rem',
                color: '#474747',
              }}
            >
              {title}
            </Typography>
          </NoFlipBox>
          {isStreaming && (
            <CircularProgress
              size="0.875rem"
              sx={{ flexShrink: 0, marginInlineStart: 'auto' }}
              thickness={2}
            />
          )}
        </ChatHistoryButton>

        {showActions && (
          <>
            <IconButton
              className={ACTIONS_CLASS}
              size="small"
              onClick={openMenu}
              aria-label={t('chatHistory.moreActions')}
              sx={{
                position: 'absolute',
                insetInlineEnd: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                padding: 0.25,
                opacity: menuAnchor ? 1 : 0,
                // A hidden-but-clickable control turns the card's trailing
                // edge into an invisible hit target for the destructive menu.
                pointerEvents: menuAnchor ? 'auto' : 'none',
                transition: 'opacity 120ms ease-in-out',
                // Touch devices have neither hover nor focus-within to reveal
                // it with, so there the kebab is simply always shown.
                '@media (hover: none)': {
                  opacity: 1,
                  pointerEvents: 'auto',
                },
              }}
            >
              <DotsThreeIcon size={16} color="#7A7A7A" />
            </IconButton>
            {menuAnchor && (
              <Menu
                anchorEl={menuAnchor}
                open
                onClose={closeMenu}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: 2,
                      boxShadow: 4,
                      border: 1,
                      borderColor: 'grey.200',
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    onDelete?.();
                  }}
                  sx={{ typography: 'textSM_regular', gap: 1, color: 'error.main' }}
                >
                  <TrashIcon size={16} />
                  {t('chatHistory.delete.menuItem')}
                </MenuItem>
              </Menu>
            )}
          </>
        )}
      </Box>
    );
  },
);
